// ABC SEO/GEO — Block 4: Keywords / Rank Tracking.
(function () {
  const { Icon } = window.SGIcons;
  const { Card, CardHead, Delta, PageHead, Gauge } = window.SGKit;

  // opportunity buckets → colour
  const BUCKET = {
    'Quick win':      { c: 'var(--sg-green)',      bg: 'var(--sg-green-50)' },
    'Fix CTR':        { c: 'var(--sg-amber)',      bg: 'var(--sg-amber-50)' },
    'Top performer':  { c: 'var(--sg-blue-600)',   bg: 'var(--sg-blue-50)' },
    'Long tail':      { c: 'var(--sg-text-3)',     bg: 'var(--sg-sunken)' },
  };
  const scoreColor = (s) => (s >= 70 ? 'var(--sg-green)' : s >= 45 ? 'var(--sg-amber)' : 'var(--sg-red)');

  // Full-width keyword-health score + opportunity breakdown.
  function KeywordScore({ a }) {
    const order = ['Quick win', 'Fix CTR', 'Top performer', 'Long tail'];
    return (
      <Card style={{ padding: 22, display: 'flex', alignItems: 'center', gap: 24 }}>
        <Gauge value={a.score} color={scoreColor(a.score)} size={132} suffix="/100" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
            <span style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 600, fontSize: 17, color: 'var(--sg-text)' }}>Keyword Score</span>
            <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '2px 8px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>
          </div>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--sg-text-2)', lineHeight: 1.5 }}>
            Scored from your real Search Console data — blends ranking strength, top-10 reach, and how your click-through compares to the expected rate for each position.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {order.filter((b) => a.buckets[b]).map((b) => (
              <span key={b} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600, color: BUCKET[b].c, background: BUCKET[b].bg, padding: '5px 12px', borderRadius: 'var(--sg-radius-pill)' }}>
                {b} <span className="sg-tnum" style={{ fontWeight: 800 }}>{a.buckets[b]}</span>
              </span>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  // Right-rail: top scored opportunities (replaces the sample "Keyword ideas").
  function LiveOpportunities({ a }) {
    const top = a.opportunities.filter((o) => o.bucket === 'Quick win' || o.bucket === 'Fix CTR').slice(0, 6);
    const list = top.length ? top : a.opportunities.slice(0, 6);
    const max = Math.max(1, ...list.map((o) => o.oppScore));
    return (
      <Card>
        <CardHead title="Keyword opportunities" sub="Biggest wins first — scored from your data"
          right={<span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-violet-700)', background: 'var(--sg-violet-100)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>SCORED</span>} />
        <div style={{ padding: '6px 14px 14px' }}>
          {list.map((o, i) => {
            const bk = BUCKET[o.bucket] || BUCKET['Long tail'];
            return (
              <div key={i} style={{ padding: '11px 6px', borderBottom: i < list.length - 1 ? '1px solid var(--sg-border)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.kw}</span>
                  <span className="sg-tnum" style={{ fontSize: 13, fontWeight: 800, color: bk.c }}>{o.oppScore}</span>
                </div>
                <div style={{ height: 5, borderRadius: 999, background: 'var(--sg-sunken)', overflow: 'hidden', margin: '6px 0' }}>
                  <div style={{ width: (o.oppScore / max * 100) + '%', height: '100%', borderRadius: 999, background: bk.c }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: bk.c, background: bk.bg, padding: '2px 7px', borderRadius: 'var(--sg-radius-pill)' }}>{o.bucket.toUpperCase()}</span>
                  <span className="sg-tnum" style={{ fontSize: 11.5, color: 'var(--sg-text-3)' }}>pos {o.position} · {o.impressions.toLocaleString()} impr</span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  /* summary stats — always fed live values via the `stats` prop */
  function SummaryRow({ stats }) {
    return (
      <Card style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', overflow: 'hidden' }}>
        {stats.map((s, i) => (
          <div key={s.label} style={{ padding: '18px 20px', borderLeft: i ? '1px solid var(--sg-border)' : 'none' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sg-text-2)', marginBottom: 8 }}>{s.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="sg-tnum" style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 26, letterSpacing: '-0.02em', color: s.accent || 'var(--sg-text)', lineHeight: 1 }}>{s.value}</span>
              {s.delta && <Delta dir={s.dir}>{s.delta}</Delta>}
            </div>
          </div>
        ))}
      </Card>
    );
  }

  /* position distribution stacked bar — always fed live values via `dist` */
  function Distribution({ dist }) {
    const total = dist.reduce((a, d) => a + d.n, 0) || 1;
    const palette = ['var(--sg-green)', 'var(--sg-blue-500)', 'var(--sg-blue-300, #7C84D6)', 'var(--sg-amber)', 'var(--sg-text-3)'];
    const D = dist.map((d, i) => ({ ...d, color: d.color || palette[i] }));
    return (
      <Card>
        <CardHead title="Position distribution" sub={`Where your ${total} ranking queries sit on Google`} />
        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', height: 16, borderRadius: 999, overflow: 'hidden', background: 'var(--sg-sunken)' }}>
            {D.map((d, i) => (
              <div key={d.label} title={`${d.label}: ${d.n}`} style={{ width: (d.n / total * 100) + '%', background: d.color,
                transformOrigin: 'left', animation: 'sg-grow 0.6s var(--sg-ease) both', animationDelay: (i * 0.08) + 's' }} />
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 22px', marginTop: 16 }}>
            {D.map((d) => (
              <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, background: d.color }} />
                <span style={{ fontSize: 12.5, color: 'var(--sg-text-2)' }}>Pos {d.label}</span>
                <span className="sg-tnum" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--sg-text)' }}>{d.n}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  /* live keyword row: real position / clicks / impressions / CTR from GSC.
     (KD, CPC and volume need a keyword database — DataForSEO, a later phase.) */
  // Matches Rank Tracking's row exactly (same columns, same null handling) — a
  // chosen keyword may have no Search Console data yet, and that's fine.
  const KWGRID = '1fr 86px 84px 80px 84px 72px';
  function LiveKwRow({ r, last }) {
    const up = r.change != null && r.change > 0;
    const down = r.change != null && r.change < 0;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: KWGRID, alignItems: 'center', gap: 12, padding: '13px 20px', borderBottom: last ? 'none' : '1px solid var(--sg-border)' }}>
        <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.kw}</span>
        <span className="sg-tnum" title={r.pos == null ? (r.checked ? 'Checked — not in Google’s top 100' : 'Not checked yet') : undefined}
          style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: r.pos == null ? 11.5 : 15, color: r.pos == null ? 'var(--sg-text-3)' : 'var(--sg-text)' }}>{r.pos != null ? Math.round(r.pos) : (r.checked ? '100+' : '—')}</span>
        <span className="sg-tnum" style={{ fontSize: 12.5, fontWeight: 700, color: up ? 'var(--sg-green)' : down ? 'var(--sg-red, #DC2626)' : 'var(--sg-text-3)' }}>{r.change == null || r.change === 0 ? '–' : (up ? '▲ ' + r.change : '▼ ' + Math.abs(r.change))}</span>
        <span className="sg-tnum" style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>{r.volume != null ? r.volume.toLocaleString() : '—'}</span>
        <span className="sg-tnum" style={{ fontSize: 13, fontWeight: 700, color: 'var(--sg-text)' }}>{r.clicks != null ? r.clicks.toLocaleString() : '—'}</span>
        <span className="sg-tnum" style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>{r.ctr != null ? (r.ctr * 100).toFixed(1) + '%' : '—'}</span>
      </div>
    );
  }

  // Empty-state for the opportunities rail before any scored opportunities exist.
  function OpportunitiesEmpty() {
    return (
      <Card>
        <CardHead title="Keyword opportunities" sub="Scored from your Search Console data" />
        <div style={{ padding: '22px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Icon name="sparkles" size={18} color="var(--sg-text-3)" />
          <div style={{ flex: 1, fontSize: 13, color: 'var(--sg-text-2)' }}>Approve keywords above and we'll surface opportunities from your Search Console data here.</div>
        </div>
      </Card>
    );
  }

  function ResearchResults({ rows }) {
    const fmtVol = (v) => v == null ? '—' : v.toLocaleString();
    const fmtCpc = (v) => v == null ? '—' : '$' + Number(v).toFixed(2);
    // difficulty: prefer real KD, else fall back to Google Ads competition_index (0–100)
    const kdOf = (r) => r.keyword_difficulty != null ? r.keyword_difficulty : (r.competition_index != null ? r.competition_index : null);
    const kdBand = (kd) => kd == null ? { t: '—', c: 'var(--sg-text-3)' } : kd >= 70 ? { t: kd + ' · Hard', c: 'var(--sg-red, #DC2626)' } : kd >= 40 ? { t: kd + ' · Medium', c: 'var(--sg-amber)' } : { t: kd + ' · Easy', c: 'var(--sg-green)' };
    const compBand = (c) => {
      if (c == null) return '—';
      if (typeof c === 'string') return c.charAt(0) + c.slice(1).toLowerCase(); // LOW → Low
      return c >= 0.66 ? 'High' : c >= 0.33 ? 'Medium' : 'Low';
    };
    return (
      <Card>
        <CardHead title="Keyword research" sub="Live Google Ads search volume, CPC & competition — from DataForSEO"
          right={<span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 100px 110px 130px', gap: 12, padding: '10px 20px', borderBottom: '1px solid var(--sg-border)', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--sg-text-3)' }}>
          <span>Keyword</span><span>Volume / mo</span><span>CPC</span><span>Competition</span><span>Difficulty</span>
        </div>
        {rows.map((r, i) => {
          const kd = kdBand(kdOf(r));
          return (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 110px 100px 110px 130px', gap: 12, padding: '12px 20px', alignItems: 'center', borderBottom: i < rows.length - 1 ? '1px solid var(--sg-border)' : 'none' }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.keyword}</span>
              <span className="sg-tnum" style={{ fontSize: 13, fontWeight: 700, color: 'var(--sg-text)' }}>{fmtVol(r.search_volume)}</span>
              <span className="sg-tnum" style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>{fmtCpc(r.cpc)}</span>
              <span style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>{compBand(r.competition)}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: kd.c }}>{kd.t}</span>
            </div>
          );
        })}
      </Card>
    );
  }

  // Canada — these clients are Canadian; gives accurate local volume/CPC.
  const LOCATION_CA = 2124;

  // Compact selectable row of a "similar" suggestion.
  function SimilarRow({ r, added, onAdd, last }) {
    const vol = r.search_volume == null ? '—' : r.search_volume.toLocaleString();
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', borderBottom: last ? 'none' : '1px solid var(--sg-border)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.keyword}</div>
          <div className="sg-tnum" style={{ fontSize: 11.5, color: 'var(--sg-text-3)', marginTop: 2 }}>{vol} / mo{r.competition_index != null ? ' · comp ' + r.competition_index : ''}</div>
        </div>
        <button onClick={() => onAdd(r.keyword)} disabled={added} aria-label="Add to list"
          style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 5, height: 30, padding: '0 12px', borderRadius: 'var(--sg-radius-pill)', border: '1px solid ' + (added ? 'var(--sg-green)' : 'var(--sg-border)'), background: added ? 'var(--sg-green-50)' : 'var(--sg-card)', color: added ? 'var(--sg-green)' : 'var(--sg-blue-600)', cursor: added ? 'default' : 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 12 }}>
          <Icon name={added ? 'check' : 'plus'} size={13} /> {added ? 'Added' : 'Add'}
        </button>
      </div>
    );
  }

  // The keyword "workbench": pick keywords pre-filled from the site (Search
  // Console), research the selected ones, then pull similar suggestions.
  const CHUNK = 25; // research a page at a time — gentler on DataForSEO credits

  function KeywordWorkbench({ perf, site, trialMode, approved, onApprove }) {
    const t = window.SGLive.transforms;
    const approvedSet = React.useMemo(() => new Set((approved || []).map((k) => k.toLowerCase())), [approved]);
    const siteRows = React.useMemo(
      () => (perf ? t.rows(perf).slice().sort((a, b) => (b.clicks - a.clicks) || (b.impressions - a.impressions)).filter((r) => !approvedSet.has((r.kw || '').toLowerCase())) : []),
      [perf, approvedSet]);
    const [selected, setSelected] = React.useState([]);
    // Candidate keywords extracted from the client's own homepage (free).
    const [webKw, setWebKw] = React.useState(null);
    React.useEffect(() => { window.SGLive.fetchWebsiteKeywords().then(setWebKw); }, []);
    const webRows = React.useMemo(
      () => (webKw || []).filter((r) => r && r.keyword && !approvedSet.has((r.keyword || '').toLowerCase())),
      [webKw, approvedSet]);
    const [custom, setCustom] = React.useState('');
    const [results, setResults] = React.useState(null);
    const [cursor, setCursor] = React.useState(0); // how many of `selected` are researched
    const [busy, setBusy] = React.useState(false);
    const [notice, setNotice] = React.useState(null);
    const [similar, setSimilar] = React.useState(null); // { seed, rows, error }
    const [simBusy, setSimBusy] = React.useState(false);
    const [approving, setApproving] = React.useState(false);
    // AI-suggested ideas matched to the business/locale/clientele (5–10 at a time).
    const [ideas, setIdeas] = React.useState(null);       // null=loading, []=none
    const [ideaMeta, setIdeaMeta] = React.useState(null); // { limit, remaining, exhausted, message }
    const [ideasBusy, setIdeasBusy] = React.useState(false);
    const shownRef = React.useRef([]);                    // ideas already offered (exclude on "show more")
    const loadIdeas = (n, append) => {
      if (ideasBusy) return;
      setIdeasBusy(true);
      const exclude = append ? shownRef.current.slice() : [];
      if (!append) shownRef.current = [];
      // Ground the AI in what the user ACTUALLY has: the keywords they added
      // (approved) as seeds, their real Search Console queries, and the selected
      // domain — so ideas are built from their keywords/site, not generic ones.
      const seeds = (approved || []).slice(0, 12);
      const realQueries = perf ? t.rows(perf).slice(0, 20).map((r) => r.kw).filter(Boolean) : [];
      window.SGLive.fetchKeywordIdeas(n, exclude, site, seeds, realQueries).then((r) => {
        setIdeasBusy(false);
        if (!r.ok) { setIdeas((prev) => prev || []); return; }
        setIdeaMeta({ limit: r.limit, remaining: r.remaining, exhausted: !!r.exhausted, message: r.message });
        if (r.exhausted) { setIdeas([]); return; }
        const incoming = r.data || [];
        shownRef.current = Array.from(new Set(shownRef.current.concat(incoming.map((x) => x.keyword))));
        setIdeas((prev) => (append && prev ? prev.concat(incoming) : incoming));
      });
    };
    React.useEffect(() => { loadIdeas(5, false); }, []); // on first open (and each visit)

    const isSel = (k) => selected.indexOf(k) >= 0;
    const toggle = (k) => setSelected((s) => (s.indexOf(k) >= 0 ? s.filter((x) => x !== k) : s.concat(k)));
    const addKw = (k) => setSelected((s) => (s.indexOf(k) >= 0 ? s : s.concat(k)));
    const addCustom = () => { const k = custom.trim().toLowerCase(); if (k) { addKw(k); setCustom(''); } };

    // Changing the selection invalidates any in-progress paged research.
    React.useEffect(() => { setResults(null); setCursor(0); setNotice(null); }, [selected]);

    // Research the next page (25), or `all` of the remaining at once.
    const research = (all) => {
      if (busy) return;
      const batch = all ? selected.slice(cursor) : selected.slice(cursor, cursor + CHUNK);
      if (!batch.length) return;
      setBusy(true); setNotice(null);
      window.SGLive.researchKeywords(batch, LOCATION_CA).then((r) => {
        setBusy(false);
        if (r.ok) {
          setResults((prev) => (prev || []).concat(r.data));
          setCursor((cur) => cur + batch.length);
          if (r.cappedTracking) setNotice({ type: 'cap', text: `You've reached your plan's tracked-rankings limit (${r.rankingLimit}). New keywords were researched but not added to tracking. Upgrade to track more.` });
        } else {
          setNotice({ type: r.error === 'keyword_limit' ? 'limit' : 'err', text: r.message });
        }
      });
    };
    const findSimilar = () => {
      const seed = selected[0] || (siteRows[0] && siteRows[0].kw) || (results && results[0] && results[0].keyword) || (approved && approved[0]);
      if (!seed || simBusy) return;
      setSimBusy(true); setSimilar({ seed, rows: null });
      window.SGLive.fetchKeywordSuggestions(seed, LOCATION_CA, 10).then((r) => {
        setSimBusy(false);
        setSimilar({ seed, rows: r.ok ? r.data : [], error: r.ok ? null : r.message });
      });
    };
    // Approve the selected keywords into the client's working set (free, no DataForSEO).
    const approve = () => {
      if (!selected.length || approving || !onApprove) return;
      setApproving(true); setNotice(null);
      onApprove(selected).then((r) => {
        setApproving(false);
        if (r && r.ok) {
          setSelected([]); setSimilar(null);
          setNotice(r.skipped
            ? { type: 'cap', text: `Added ${r.added} of ${r.requested}. ${r.skipped} skipped — you're at your plan's limit of ${r.limit} tracked keywords (${r.used} used across all your domains). Remove some below, or upgrade to track more.` }
            : { type: 'ok', text: `Added ${r.added} keyword${r.added === 1 ? '' : 's'} to your approved list — checking their Google rankings now.` });
        } else {
          setNotice({ type: 'err', text: (r && r.message) || 'Could not approve those keywords.' });
        }
      });
    };

    const haveSite = siteRows.length > 0;
    // Recompute "slots left" client-side against the live approved count so the
    // exhausted state updates the moment they approve, without another AI call.
    const remainingNow = ideaMeta && typeof ideaMeta.limit === 'number'
      ? Math.max(0, ideaMeta.limit - ((approved && approved.length) || 0))
      : (ideaMeta ? ideaMeta.remaining : null);
    const ideasExhausted = !!(ideaMeta && (ideaMeta.exhausted || remainingNow === 0));
    const visibleIdeas = (ideas || []).filter((x) => x && x.keyword && !approvedSet.has((x.keyword || '').toLowerCase()));
    return (
      <React.Fragment>
        <Card>
          <CardHead title="Build your keyword list"
            sub={haveSite ? 'Pre-filled from your Search Console queries — tick the ones you want to research.' : 'Add the keywords you want to research, or connect Search Console to pre-fill from your site.'}
            right={<span style={{ fontSize: 11.5, fontWeight: 700, color: selected.length ? 'var(--sg-blue-600)' : 'var(--sg-text-3)', background: selected.length ? 'var(--sg-blue-50)' : 'var(--sg-sunken)', padding: '3px 11px', borderRadius: 'var(--sg-radius-pill)' }}>{selected.length} selected</span>} />
          <div style={{ padding: 18 }}>
            {/* selected chips */}
            {selected.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                {selected.map((k) => (
                  <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--sg-text)', background: 'var(--sg-blue-50)', border: '1px solid var(--sg-blue-100, #CFE0FF)', padding: '4px 6px 4px 12px', borderRadius: 'var(--sg-radius-pill)' }}>
                    {k}
                    <button onClick={() => toggle(k)} aria-label="Remove" style={{ width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--sg-text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="x" size={13} /></button>
                  </span>
                ))}
              </div>
            )}

            {/* AI-suggested keyword ideas — matched to the business, locale & clientele */}
            {(ideas === null || ideasBusy || visibleIdeas.length > 0 || ideasExhausted) && (
              <div style={{ marginBottom: 16, border: '1px solid var(--sg-violet-200, #DDD6FE)', borderRadius: 'var(--sg-radius-md)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', background: 'var(--sg-violet-50)' }}>
                  <Icon name="sparkles" size={15} color="var(--sg-violet-600)" />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--sg-violet-700)' }}>Suggested for you</span>
                  <span style={{ fontSize: 11.5, color: 'var(--sg-text-3)' }}>matched to your business &amp; location</span>
                  {ideaMeta && !ideasExhausted && remainingNow != null && (
                    <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: 'var(--sg-text-3)' }}>{remainingNow} slot{remainingNow === 1 ? '' : 's'} left</span>
                  )}
                </div>

                {ideasExhausted ? (
                  <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--sg-text-2)', lineHeight: 1.5 }}>
                    {(ideaMeta && ideaMeta.message) || 'You’ve reached your keyword limit. Remove a few keywords below to get fresh suggestions.'}
                  </div>
                ) : (
                  <React.Fragment>
                    {(ideas === null || (ideasBusy && visibleIdeas.length === 0)) && (
                      <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--sg-text-2)' }}>Finding good keywords for your business…</div>
                    )}
                    {visibleIdeas.map((it) => (
                      <label key={it.keyword} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', borderTop: '1px solid var(--sg-border)', cursor: 'pointer', background: isSel(it.keyword) ? 'var(--sg-blue-50)' : 'transparent' }}>
                        <input type="checkbox" checked={isSel(it.keyword)} onChange={() => toggle(it.keyword)} style={{ width: 16, height: 16, accentColor: 'var(--sg-blue-600)', cursor: 'pointer', flex: 'none' }} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 13.5, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.keyword}</span>
                          {it.why && <span style={{ display: 'block', fontSize: 11, color: 'var(--sg-text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.why}</span>}
                        </span>
                        {it.intent && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--sg-violet-700)', background: 'var(--sg-card)', border: '1px solid var(--sg-violet-100)', padding: '2px 7px', borderRadius: 'var(--sg-radius-pill)', flex: 'none' }}>{it.intent}</span>}
                      </label>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderTop: '1px solid var(--sg-border)' }}>
                      <button onClick={() => loadIdeas(5, true)} disabled={ideasBusy || remainingNow === 0}
                        style={{ height: 34, padding: '0 14px', border: '1px solid var(--sg-violet-200, #DDD6FE)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', color: 'var(--sg-violet-700)', cursor: (ideasBusy || remainingNow === 0) ? 'default' : 'pointer', opacity: (ideasBusy || remainingNow === 0) ? 0.55 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 12.5 }}>
                        {ideasBusy ? 'Finding…' : '+ 5 more'}
                      </button>
                      <button onClick={() => loadIdeas(10, true)} disabled={ideasBusy || remainingNow === 0}
                        style={{ height: 34, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', color: 'var(--sg-text-2)', cursor: (ideasBusy || remainingNow === 0) ? 'default' : 'pointer', opacity: (ideasBusy || remainingNow === 0) ? 0.55 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 12.5 }}>
                        + 10 more
                      </button>
                      {visibleIdeas.length > 0 && (
                        <button onClick={() => setSelected((s) => Array.from(new Set(s.concat(visibleIdeas.map((x) => x.keyword)))))}
                          style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: 'var(--sg-blue-600)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Select all</button>
                      )}
                    </div>
                  </React.Fragment>
                )}
              </div>
            )}

            {/* site keyword list (from GSC) */}
            {haveSite && (
              <React.Fragment>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--sg-text-3)' }}>From your site</span>
                  <button onClick={() => setSelected(siteRows.slice(0, 10).map((r) => r.kw))} style={{ fontSize: 12, fontWeight: 600, color: 'var(--sg-blue-600)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Select top 10</button>
                  {selected.length > 0 && <button onClick={() => setSelected([])} style={{ fontSize: 12, fontWeight: 600, color: 'var(--sg-text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Clear</button>}
                </div>
                <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)' }}>
                  {siteRows.slice(0, 60).map((r, i, arr) => (
                    <label key={r.kw} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', borderBottom: i < arr.length - 1 ? '1px solid var(--sg-border)' : 'none', cursor: 'pointer', background: isSel(r.kw) ? 'var(--sg-blue-50)' : 'transparent' }}>
                      <input type="checkbox" checked={isSel(r.kw)} onChange={() => toggle(r.kw)} style={{ width: 16, height: 16, accentColor: 'var(--sg-blue-600)', cursor: 'pointer', flex: 'none' }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.kw}</span>
                      <span className="sg-tnum" style={{ fontSize: 11.5, color: 'var(--sg-text-3)', flex: 'none' }}>pos {r.pos.toFixed(0)} · {r.impressions.toLocaleString()} impr</span>
                    </label>
                  ))}
                </div>
              </React.Fragment>
            )}

            {/* candidate keywords from the client's own website */}
            {webRows.length > 0 && (
              <React.Fragment>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '14px 0 8px' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--sg-text-3)' }}>From your website</span>
                  <button onClick={() => setSelected((s) => Array.from(new Set(s.concat(webRows.slice(0, 10).map((r) => r.keyword)))))} style={{ fontSize: 12, fontWeight: 600, color: 'var(--sg-blue-600)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Select top 10</button>
                </div>
                <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)' }}>
                  {webRows.slice(0, 40).map((r, i, arr) => (
                    <label key={r.keyword} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', borderBottom: i < arr.length - 1 ? '1px solid var(--sg-border)' : 'none', cursor: 'pointer', background: isSel(r.keyword) ? 'var(--sg-blue-50)' : 'transparent' }}>
                      <input type="checkbox" checked={isSel(r.keyword)} onChange={() => toggle(r.keyword)} style={{ width: 16, height: 16, accentColor: 'var(--sg-blue-600)', cursor: 'pointer', flex: 'none' }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.keyword}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--sg-text-3)', background: 'var(--sg-sunken)', padding: '2px 7px', borderRadius: 'var(--sg-radius-pill)', flex: 'none' }}>{r.source}</span>
                    </label>
                  ))}
                </div>
              </React.Fragment>
            )}

            {/* manual add */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
              <input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addCustom(); }}
                placeholder="Add another keyword…"
                style={{ flex: 1, height: 42, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 13.5, color: 'var(--sg-text)', outline: 'none' }} />
              <button onClick={addCustom} disabled={!custom.trim()} style={{ height: 42, padding: '0 16px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', color: 'var(--sg-blue-600)', cursor: custom.trim() ? 'pointer' : 'not-allowed', opacity: custom.trim() ? 1 : 0.5, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13 }}>Add</button>
            </div>

            {/* trial allowance note */}
            {trialMode && (
              <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-amber-50)', border: '1px solid var(--sg-amber-100, #F6E6C7)', fontSize: 12.5, color: 'var(--sg-text)' }}>
                <strong>Trial:</strong> 25 keywords &amp; 25 tracked rankings included. Upgrade to research more.
              </div>
            )}

            {/* Approve (free) is the primary action; research volume/difficulty is optional */}
            <button onClick={approve} disabled={!selected.length || approving}
              style={{ marginTop: 14, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 48, border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: (!selected.length || approving) ? 'not-allowed' : 'pointer', opacity: (!selected.length || approving) ? 0.55 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 14.5, color: '#fff', background: 'var(--sg-grad-brand)', boxShadow: '0 3px 10px rgba(37,99,235,0.26)' }}>
              <Icon name="check" size={17} /> {approving ? 'Approving…' : selected.length ? `Approve ${selected.length} keyword${selected.length === 1 ? '' : 's'}` : 'Select keywords to approve'}
            </button>
            <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <button onClick={findSimilar} disabled={simBusy || (!selected.length && !(approved && approved.length) && !results)}
                style={{ flex: 1, minWidth: 170, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, border: '1px solid var(--sg-violet-200, #DDD6FE)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-violet-50)', color: 'var(--sg-violet-700)', cursor: simBusy ? 'default' : 'pointer', opacity: simBusy ? 0.6 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13 }}>
                <Icon name="sparkles" size={15} /> {simBusy ? 'Finding…' : 'Suggest 10 more similar'}
              </button>
              {!trialMode && (
                <button onClick={() => research(false)} disabled={busy || (selected.length - cursor) <= 0}
                  style={{ flex: 1, minWidth: 170, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', color: 'var(--sg-text-2)', cursor: (busy || (selected.length - cursor) <= 0) ? 'not-allowed' : 'pointer', opacity: (busy || (selected.length - cursor) <= 0) ? 0.5 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13 }}>
                  <Icon name="search" size={15} /> {busy ? 'Researching…' : 'Research volume & difficulty'}
                </button>
              )}
            </div>
          </div>
        </Card>

        {notice && (
          <Card style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 11,
            background: notice.type === 'ok' ? 'var(--sg-green-50)' : notice.type === 'limit' ? 'var(--sg-violet-50)' : notice.type === 'cap' ? 'var(--sg-amber-50)' : 'var(--sg-red-50, #FDECEC)',
            border: '1px solid ' + (notice.type === 'ok' ? 'var(--sg-green-100, #C9F0D6)' : notice.type === 'limit' ? 'var(--sg-violet-100)' : notice.type === 'cap' ? 'var(--sg-amber-100, #F6E6C7)' : 'var(--sg-red-100, #F8C9C9)') }}>
            <Icon name={notice.type === 'err' ? 'alert-triangle' : notice.type === 'ok' ? 'check' : 'sparkles'} size={18} color={notice.type === 'ok' ? 'var(--sg-green)' : notice.type === 'limit' ? 'var(--sg-violet-600)' : notice.type === 'cap' ? 'var(--sg-amber)' : 'var(--sg-red, #DC2626)'} />
            <div style={{ flex: 1, fontSize: 13, color: 'var(--sg-text)' }}>{notice.text}</div>
            {(notice.type === 'limit' || notice.type === 'cap') && (
              <button onClick={() => { try { window.parent && window.parent.postMessage({ type: 'abc-upgrade' }, '*'); } catch (e) { /* noop */ } }}
                style={{ flex: 'none', height: 34, padding: '0 14px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-violet, var(--sg-grad-brand))' }}>Upgrade</button>
            )}
          </Card>
        )}

        {results && (results.length > 0
          ? (
            <React.Fragment>
              <ResearchResults rows={results} />
              <Card style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Icon name="sparkles" size={18} color="var(--sg-violet-600)" />
                <div style={{ flex: 1, minWidth: 200, fontSize: 13, color: 'var(--sg-text-2)' }}>Want more ideas? Pull keywords similar to <strong style={{ color: 'var(--sg-text)' }}>{(results[0] && results[0].keyword) || selected[0]}</strong>.</div>
                <button onClick={findSimilar} disabled={simBusy}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 18px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: simBusy ? 'default' : 'pointer', opacity: simBusy ? 0.6 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13.5, color: '#fff', background: 'var(--sg-grad-violet, var(--sg-grad-brand))' }}>
                  <Icon name="sparkles" size={15} /> {simBusy ? 'Finding…' : 'Find similar keywords'}
                </button>
              </Card>
            </React.Fragment>
          )
          : (!notice && <Card style={{ padding: 16, fontSize: 13, color: 'var(--sg-text-2)' }}>No volume data returned for those keywords. Try broader terms.</Card>))}

        {similar && (
          <Card>
            <CardHead ai title="Similar keywords" sub={`Related searches based on “${similar.seed}” — add any to your list`} />
            <div style={{ padding: '6px 14px 14px' }}>
              {similar.rows === null && <div style={{ padding: 10, fontSize: 13, color: 'var(--sg-text-2)' }}>Finding related keywords…</div>}
              {similar.rows && similar.rows.length === 0 && <div style={{ padding: 10, fontSize: 13, color: 'var(--sg-text-2)' }}>{similar.error || 'No related keywords found for that seed.'}</div>}
              {similar.rows && similar.rows.filter((r) => r.keyword && r.keyword !== similar.seed).slice(0, 20).map((r, i, arr) => (
                <SimilarRow key={r.keyword} r={r} added={isSel(r.keyword)} onAdd={addKw} last={i === arr.length - 1} />
              ))}
            </div>
          </Card>
        )}
      </React.Fragment>
    );
  }

  // "Your keywords" — the approved working set, with remove + add-more toggle.
  function ApprovedManager({ approved, onRemove, onAddMore, builderOpen, checking, checkMsg }) {
    return (
      <Card>
        <CardHead title={`Your keywords (${approved.length})`} sub={checking ? 'Checking these keywords’ Google positions…' : 'Only these approved keywords are tracked, researched and scored.'}
          right={<button onClick={onAddMore} disabled={checking} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 14px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: checking ? 'wait' : 'pointer', opacity: checking ? 0.7 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 12.5, color: '#fff', background: 'var(--sg-grad-brand)' }}><Icon name={builderOpen ? 'check' : 'plus'} size={14} /> {checking ? 'Checking rankings…' : (builderOpen ? 'Done adding' : 'Add / suggest more')}</button>} />
        {checkMsg && <div style={{ padding: '0 18px', marginTop: -4, fontSize: 12.5, color: checkMsg.ok ? 'var(--sg-green)' : 'var(--sg-red, #DC2626)' }}>{checkMsg.text}</div>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 18 }}>
          {approved.map((k) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600, color: 'var(--sg-text)', background: 'var(--sg-green-50)', border: '1px solid var(--sg-green-100, #C9F0D6)', padding: '5px 6px 5px 12px', borderRadius: 'var(--sg-radius-pill)' }}>
              {k}
              <button onClick={() => onRemove(k)} aria-label="Remove" style={{ width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--sg-text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="x" size={12} /></button>
            </span>
          ))}
        </div>
      </Card>
    );
  }

  function Keywords() {
    const { sites, site, setSite, perf, prettySite } = window.SGLive.useLive();
    const t = window.SGLive.transforms;
    const ent = window.SGLive.useEntitlement();
    // Trial accounts get a small keyword allowance (25) — only canceled/past_due
    // are fully locked out of keyword research.
    const trialMode = ent.status === 'trial';
    const locked = ent.paidData === false && !trialMode;

    // Approved working set — drives every panel once the client approves any keyword.
    const [approved, setApproved] = React.useState(null); // null = loading
    const reloadApproved = React.useCallback(() => window.SGLive.fetchApprovedKeywords().then(setApproved), []);
    // Reload the approved set when the selected domain changes — each domain has
    // its own list now. setApproved(null) shows the loading state during the swap.
    React.useEffect(() => { setApproved(null); reloadApproved(); }, [reloadApproved, site]);
    const onApprove = React.useCallback((list) => window.SGLive.approveKeywords(list).then((r) => { if (r && r.ok) reloadApproved(); return r; }), [reloadApproved]);
    const onRemove = React.useCallback((kw) => window.SGLive.removeApprovedKeyword(kw).then(() => reloadApproved()), [reloadApproved]);
    const [showBuilder, setShowBuilder] = React.useState(false);
    // Clicking "Done adding" runs the ranking analysis for the whole approved set
    // — the natural moment to check where the newly-added keywords stand.
    const [checking, setChecking] = React.useState(false);
    const [checkMsg, setCheckMsg] = React.useState(null);
    const toggleBuilder = React.useCallback(() => {
      setShowBuilder((open) => {
        if (open && !checking) {   // closing = "Done adding" → check rankings now
          setChecking(true); setCheckMsg(null);
          window.SGLive.checkRanks().then((r) => {
            setChecking(false);
            if (r && r.ok) {
              setCheckMsg({ ok: true, text: r.queued
                ? `Checking ${r.queued} keyword${r.queued === 1 ? '' : 's'} against Google in the background — positions appear over the next few minutes.`
                : `Checked ${r.checked} keyword${r.checked === 1 ? '' : 's'} — ${r.found} ranking in Google's top 100.` });
              reloadTracked();
              if (r.queued) { setTimeout(reloadTracked, 20000); setTimeout(reloadTracked, 60000); }
            } else if (r && r.error) setCheckMsg({ ok: false, text: r.error });
          });
        }
        return !open;
      });
      /* eslint-disable-next-line */
    }, [checking]);
    const hasApproved = !!(approved && approved.length);
    const builderOpen = !hasApproved || showBuilder;
    const approvedSet = React.useMemo(() => new Set((approved || []).map((k) => k.toLowerCase())), [approved]);

    // The CHOSEN keywords + their real positions — the SAME source Rank Tracking
    // reads, via the same shared helpers, so both screens always agree.
    const [tracked, setTracked] = React.useState(null);
    const reloadTracked = React.useCallback(() => window.SGLive.fetchTrackedKeywords().then(setTracked), []);
    React.useEffect(() => { setTracked(null); reloadTracked(); }, [reloadTracked, site, approved]);

    const live = !!perf;
    const trackedRows = React.useMemo(() => t.trackedRows(tracked, perf), [tracked, perf, t]);
    const tsum = React.useMemo(() => t.trackedSummary(trackedRows), [trackedRows, t]);
    // Once keywords are approved, all analytics reflect ONLY the approved set.
    const effPerf = (live && hasApproved)
      ? Object.assign({}, perf, { byQuery: (perf.byQuery || []).filter((r) => approvedSet.has(((r.keys && r.keys[0]) || '').toLowerCase())) })
      : perf;
    const sum = live ? t.summary(effPerf) : null;
    const liveSummary = hasApproved && trackedRows ? [
      { label: 'Your keywords', value: String(tsum.total) },
      { label: 'Top 3 positions', value: String(tsum.top3), accent: 'var(--sg-green)' },
      { label: 'Top 10', value: String(tsum.top10) },
      { label: 'Avg. position', value: tsum.avgPos != null ? tsum.avgPos.toFixed(1) : '—' },
      { label: 'Clicks · 28d', value: tsum.clicks.toLocaleString(), mono: true },
    ] : live ? [
      { label: 'Ranking queries', value: sum.tracked.toLocaleString() },
      { label: 'Top 3 positions', value: String(sum.top3), accent: 'var(--sg-green)' },
      { label: 'Top 10', value: String(sum.top10) },
      { label: 'Avg. position', value: sum.avgPos.toFixed(1) },
      { label: 'Clicks · 28d', value: sum.clicks.toLocaleString(), mono: true },
    ] : null;
    const rows = live ? t.rows(effPerf) : null;
    const kwa = live ? t.keywordAnalysis(effPerf) : null;
    return (
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <PageHead eyebrow="Research" title="Keywords" icon="key"
          sub={hasApproved ? `Your approved keyword set — tracked, researched and scored${live ? ' for ' + prettySite(site) : ''}.` : 'Approve the keywords you want to focus on — then only those are tracked and scored.'}
          right={live
            ? (
              <span title="Switch domains from the picker at the top of the page"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-sunken)', fontSize: 13, fontWeight: 600, color: 'var(--sg-text-2)' }}>
                <Icon name="globe" size={14} /> {prettySite(site)}
              </span>
            )
            : (
              <div style={{ display: 'inline-flex', background: 'var(--sg-sunken)', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', padding: 3 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 36, padding: '0 14px', fontSize: 13, fontWeight: 600, color: 'var(--sg-text)', background: 'var(--sg-card)', borderRadius: 'var(--sg-radius-pill)', boxShadow: 'var(--sg-shadow)' }}><Icon name="globe" size={15} color="var(--sg-text-2)" /> Canada</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', height: 36, padding: '0 14px', fontSize: 13, fontWeight: 600, color: 'var(--sg-text-2)' }}>Mobile</span>
              </div>
            )} />

        {/* Approved set manager — shown once the client has approved any keywords */}
        {hasApproved && <ApprovedManager approved={approved} onRemove={onRemove} onAddMore={toggleBuilder} builderOpen={showBuilder} checking={checking} checkMsg={checkMsg} />}

        {/* Build / approve flow (free approval + optional paid research/suggestions) */}
        {locked && <window.SGLive.PaidLock title="Keyword research unlocks on a paid plan" blurb="Live Google search volume, CPC, competition and keyword difficulty use premium data. You can still approve keywords and see your real Search Console rankings and scoring below." />}
        {builderOpen && (
          <React.Fragment>
            {hasApproved && <div style={{ fontSize: 12.5, color: 'var(--sg-text-3)', margin: '-8px 2px 0' }}>Tick more keywords and approve them, or ask for 10 similar suggestions.</div>}
            <KeywordWorkbench perf={perf} site={site} trialMode={trialMode} approved={approved || []} onApprove={onApprove} />
          </React.Fragment>
        )}

        {live ? (
          (hasApproved && rows.length === 0) ? (
            <Card style={{ padding: '22px', display: 'flex', alignItems: 'center', gap: 12, background: 'var(--sg-sunken)' }}>
              <Icon name="key" size={18} color="var(--sg-text-3)" />
              <div style={{ flex: 1, fontSize: 13, color: 'var(--sg-text-2)' }}>Your approved keywords don't have Search Console impressions yet for {prettySite(site)} — ranking data will appear here as Google records them. Use “Research volume &amp; difficulty” in the builder for their search data now.</div>
            </Card>
          ) : (
            <React.Fragment>
              <SummaryRow stats={liveSummary} />
              {kwa.opportunities.length > 0 && <KeywordScore a={kwa} />}
              <Distribution dist={hasApproved && trackedRows ? t.trackedDistribution(trackedRows) : t.distribution(effPerf)} />
              <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 20, alignItems: 'start' }}>
                <Card>
                  {(() => {
                    // Once they've chosen keywords, show THOSE (same rows and same
                    // numbers as Rank Tracking). Otherwise fall back to GSC queries.
                    const useTracked = hasApproved && trackedRows;
                    const list = useTracked ? trackedRows : (rows || []).map((r) => ({ ...r, change: null, volume: null }));
                    const cap = Math.min(25, list.length);
                    return (
                      <React.Fragment>
                        <CardHead title={hasApproved ? 'Your keyword rankings' : 'Ranking queries'}
                          sub={useTracked ? `The keywords you chose for ${prettySite(site)} · position from Google, clicks from Search Console` : `Real Search Console data for ${prettySite(site)} · last 28 days`}
                          right={<span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>} />
                        <div style={{ display: 'grid', gridTemplateColumns: KWGRID, gap: 12, padding: '10px 20px', borderBottom: '1px solid var(--sg-border)', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--sg-text-3)' }}>
                          <span>Keyword</span><span>Position</span><span>Change</span><span>Volume</span><span>Clicks</span><span>CTR</span>
                        </div>
                        {list.slice(0, 25).map((r, i) => <LiveKwRow key={i} r={r} last={i === cap - 1} />)}
                      </React.Fragment>
                    );
                  })()}
                </Card>
                {kwa.opportunities.length > 0 ? <LiveOpportunities a={kwa} /> : <OpportunitiesEmpty />}
              </div>
            </React.Fragment>
          )
        ) : (
          <Card style={{ padding: '22px', display: 'flex', alignItems: 'center', gap: 12, background: 'var(--sg-sunken)' }}>
            <Icon name="key" size={18} color="var(--sg-text-3)" />
            <div style={{ flex: 1, fontSize: 13, color: 'var(--sg-text-2)' }}>Connect Google Search Console (Settings → Connections) to pre-fill candidate keywords and see live rankings, scoring and distribution. You can still approve keywords manually above.</div>
          </Card>
        )}
      </div>
    );
  }

  window.SGKeywords = Keywords;
})();
